import  { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { AuthContext } from '../../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';

// Icons
import { ChevronLeft, ChevronRight, Terminal, Cpu, Share2 } from "lucide-react";
import { 
  HiDownload, 
  HiUser, 
  HiChatAlt2,
  HiCreditCard,
  HiLightningBolt,
  HiLink
} from 'react-icons/hi';

import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaPlay, FaYoutube } from 'react-icons/fa';


// --- THEME PALETTE ---
const COLORS = {
  bg: "#020617",         // Deep Space
  panel: "#0f172a",      // Slate 900
  accent: "#22d3ee",     // Electric Cyan
  gold: "#facc15",       // Yellow-400 (Commerce)
  textMain: "#94a3b8",   // Slate 400
  textLight: "#f1f5f9",  // Slate 100
};

const trackGAEvent = (category, action, label) => {
  ReactGA.event({
    category: category,
    action: action,
    label: label, 
  });
};

const ProductDetails = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  
  // Slider State
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Comment State
  const [newComment, setNewComment] = useState('');
  const [username, setUsername] = useState('');
  const [contact, setContact] = useState('');

  const fetchBlogDetails = async () => {
    try {
      const { data } = await axiosInstance.get(`/products/${id}`);
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product details:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBlogDetails();
      axiosInstance.put(`/products/${id}/click`).catch(err => console.error("Failed to increment view count.", err));
    }
  }, [id]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !username.trim()) return;

    trackGAEvent('Engagement', 'Submit Comment', product.title);

    try {
      await axiosInstance.post(`/products/${id}/comment`, {
        comment: newComment,
        username: username,
        contact: contact,
      });

      fetchBlogDetails(); 
      setNewComment('');
      setUsername('');
      setContact('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  if (!product) {
    return (
        <div className="min-h-screen bg-[#020617] flex items-center justify-center text-[#22d3ee] font-mono animate-pulse">
            LOADING_DATA_STREAM...
        </div>
    );
  }

  // --- HELPER: Extract YouTube Video ID ---
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    // Standard embed for watching (controls enabled, no auto-mute)
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
  };
  
  // --- MEDIA SLIDER LOGIC ---
  const mediaList = [];

  // 1. Uploaded Video (Priority 1)
  if (+product.isVideo === 1 && product.video_link) {
    mediaList.push({ type: "video", src: product.video_link });
  }

  // 2. YouTube Video (Priority 2) - NEW
  if (product.yt_link) {
    const embedUrl = getYouTubeEmbedUrl(product.yt_link);
    if (embedUrl) {
      mediaList.push({ type: "youtube", src: embedUrl });
    }
  }

  // 3. Images (Priority 3)
  if (Array.isArray(product.images) && product.images.length > 0) {
    product.images.forEach((img) => mediaList.push({ type: "image", src: img }));
  } else if (product.image) {
    mediaList.push({ type: "image", src: product.image });
  }

  const nextSlide = (e) => {
    if(e) e.preventDefault();
    setCurrentSlide((prev) => (prev === mediaList.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e) => {
    if(e) e.preventDefault();
    setCurrentSlide((prev) => (prev === 0 ? mediaList.length - 1 : prev - 1));
  };

  // SEO
  const plainTextDescription = product.content.replace(/<[^>]*>?/gm, '').substring(0, 160);
  const currentUrl = window.location.href;
  const keywords = `${product.title.split(' ').join(', ')}, ${product.category_name}`;
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "description": plainTextDescription,
    "image": product.image || "",
    "offers": { "@type": "Offer", "price": product.price || "0", "priceCurrency": "USD" }
  };

  
  return (
    <>
      <Helmet>
        <title>{product.title}</title>
        <meta name="description" content={plainTextDescription} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={currentUrl} />
        <script type="application/ld+json">{JSON.stringify(articleStructuredData)}</script>
      </Helmet>

      <div className="bg-[#020617] min-h-screen py-10 relative overflow-hidden font-sans text-slate-300">
        
        {/* --- DECORATIVE BACKGROUND --- */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(34,211,238,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.02)_1px,transparent_1px)] bg-[size:30px_30px] pointer-events-none"></div>

        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          
          {/* Breadcrumb / Top Bar */}
          <div className="flex items-center gap-2 text-xs font-mono text-[#22d3ee] mb-6 uppercase tracking-widest">
            <Terminal size={14} />
            <span>Root</span> / <span>Products</span> / <span className="text-white">{product.title.substring(0,20)}...</span>
          </div>

          {/* Main Card */}
          <div className="bg-[#0f172a] border border-[#22d3ee]/20 shadow-[0_0_50px_-20px_rgba(34,211,238,0.1)] overflow-hidden">
            
            <div className="md:flex">
                {/* --- LEFT SIDE: MEDIA DISPLAY --- */}
                <div className="md:w-3/5 bg-[#020617] relative border-r border-[#22d3ee]/10 flex flex-col">
                      
                      {/* 1. MAIN VIEWER (Slider) */}
                      <div className="relative w-full aspect-[16/10] bg-black group overflow-hidden">
                        
                        {/* Tech Corner Overlays */}
                        <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-[#22d3ee] z-20 pointer-events-none"></div>
                        <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-[#22d3ee] z-20 pointer-events-none"></div>
                        <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-[#22d3ee] z-20 pointer-events-none"></div>
                        <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-[#22d3ee] z-20 pointer-events-none"></div>
                        
                        {/* Scanline Overlay */}
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_2px,3px_100%] pointer-events-none opacity-20"></div>

                        {/* Media Renderer */}
                        <div className="w-full h-full relative">
                            {mediaList.map((media, index) => (
                                <div
                                key={index}
                                className={`absolute inset-0 w-full h-full transition-all duration-700 ease-in-out transform ${
                                    index === currentSlide ? "opacity-100 scale-100 z-10" : "opacity-0 scale-105 z-0"
                                }`}
                                >
                                {media.type === "video" ? (
                                    <video src={media.src} className="w-full h-full object-contain" controls autoPlay loop muted playsInline />
                                ) : media.type === "youtube" ? (
                                    <iframe 
                                        src={media.src} 
                                        title="YouTube video player"
                                        className="w-full h-full object-contain" 
                                        frameBorder="0" 
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                        allowFullScreen
                                    ></iframe>
                                ) : (
                                    <img src={media.src} alt={product.title} className="w-full h-full object-contain" />
                                )}
                                </div>
                            ))}
                        </div>

                        {/* Navigation Arrows */}
                        {mediaList.length > 1 && (
                        <>
                            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-[#0f172a]/80 border border-[#22d3ee]/50 p-2 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-[#020617] transition-all backdrop-blur-sm group-hover:opacity-100 opacity-0">
                                <ChevronLeft size={24} />
                            </button>
                            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-[#0f172a]/80 border border-[#22d3ee]/50 p-2 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-[#020617] transition-all backdrop-blur-sm group-hover:opacity-100 opacity-0">
                                <ChevronRight size={24} />
                            </button>
                        </>
                        )}
                    </div>

                    {/* 2. THUMBNAIL SELECTOR */}
                    {mediaList.length > 1 && (
                        <div className="p-4 bg-[#0f172a] border-t border-[#22d3ee]/10 flex gap-3 overflow-x-auto scrollbar-hide">
                            {mediaList.map((media, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`relative flex-shrink-0 w-20 h-14 overflow-hidden border transition-all duration-300 ${
                                        currentSlide === index 
                                        ? 'border-[#22d3ee] shadow-[0_0_10px_rgba(34,211,238,0.5)]' 
                                        : 'border-white/10 opacity-50 hover:opacity-100'
                                    }`}
                                >
                                    {media.type === 'video' ? (
                                        <div className="w-full h-full bg-black flex items-center justify-center">
                                            <FaPlay className="text-white/50 text-xs" />
                                        </div>
                                    ) : media.type === 'youtube' ? (
                                        <div className="w-full h-full bg-black flex items-center justify-center relative">
                                            <div className="absolute inset-0 bg-red-600/10"></div>
                                            <FaYoutube className="text-red-500 text-lg z-10" />
                                        </div>
                                    ) : (
                                        <img src={media.src} alt="thumb" className="w-full h-full object-cover" />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* --- RIGHT SIDE: DETAILS --- */}
                <div className="md:w-2/5 p-8 flex flex-col bg-[#0f172a]">
                    <div className="mb-8 relative">
                        {/* Decorative Line */}
                        <div className="absolute -left-8 top-2 w-1 h-12 bg-[#22d3ee]"></div>
                        
                        <h1 className="text-3xl font-bold text-white leading-tight mb-2 tracking-wide font-sans">
                            {product.title}
                        </h1>
                        <p className="text-3xl text-[#22d3ee] font-mono tracking-tighter flex items-center gap-2">
                            {product.price ? `₹${product.price}` : 'FREE_ACCESS'}
                            <span className="text-xs text-slate-500 font-sans tracking-normal ml-2 py-1 px-2 border border-slate-700 rounded">v.2.0</span>
                        </p>
                    </div>

                    <div className="space-y-4">
                        {/* Payment Link (Gold/Yellow Accent for High Priority) */}
                        {product.pay_link && (
                            <a 
                                href={product.pay_link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="group flex items-center justify-center gap-3 bg-[#facc15]/10 border border-[#facc15] text-[#facc15] font-bold py-4 px-6 hover:bg-[#facc15] hover:text-black transition-all duration-300 w-full uppercase tracking-[0.2em] text-xs relative overflow-hidden"
                                onClick={() => trackGAEvent('Engagement', 'Payment Link Click', product.title)}
                            >
                                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 skew-y-12"></div>
                                <HiCreditCard size={18} className="relative z-10"/>
                                <span className="relative z-10">{product.price ? ` Acquire License` : 'Initialize Download'}</span>
                            </a>
                        )}
                         {product.live_link && (
                            <a 
                                href={product.live_link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="group flex items-center justify-center gap-3 bg-[#facc15]/10 border border-[#facc15] text-[#facc15] font-bold py-4 px-6 hover:bg-[#facc15] hover:text-black transition-all duration-300 w-full uppercase tracking-[0.2em] text-xs relative overflow-hidden"
                                onClick={() => trackGAEvent('Engagement', 'Payment Link Click', product.title)}
                            >
                                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 skew-y-12"></div>
                                <HiLink size={18} className="relative z-10"/>
                                <span className="relative z-10">Live View</span>
                            </a>
                        )}

                          {/* Main Tech (Protocol Tag) */}
         {product.main_tech_name && (
            <div className="mb-3">
               <span className="inline-flex items-center text-[19px] font-mono text-[#22d3ee]/80 border-l-2 border-[#22d3ee]/50 pl-2">
                 CORE: <span className="text-white ml-1 uppercase">{product.main_tech_name}</span>
               </span>
            </div>
         )}

         {/* Tech Stack (Data Chips) */}
         {product.tech_stack && (
            <div className="flex flex-wrap gap-1.5 mb-4">
               {product.tech_stack.split(",").map((tech, i) => (
                   <span 
                    key={i}  style={{textTransform:"capitalize"}}
                    className="text-[15px] font-medium text-slate-400 bg-slate-800/50 px-2 py-0.5 rounded-sm border border-slate-700/50 hover:border-[#22d3ee]/30 hover:text-[#22d3ee] transition-colors cursor-default whitespace-nowrap"
                   >
                       {tech.trim()}
                   </span>
               ))}
            </div>
         )}

                        {/* PDF Download (Cyan Accent) */}
                        {product.pdf_drive_link && (
                        <a 
                            href={product.pdf_drive_link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="group flex items-center justify-center gap-2 bg-[#22d3ee]/5 border border-[#22d3ee]/50 text-[#22d3ee] font-bold py-3 px-4 hover:bg-[#22d3ee]/10 hover:shadow-[0_0_15px_rgba(34,211,238,0.3)] transition-all duration-300 w-full font-mono text-xs"
                            onClick={() => trackGAEvent('Download', 'PDF Download Click', product.title)}
                        >
                            <HiDownload size={16} /> DOWNLOAD_DOCUMENTATION.PDF
                        </a>
                        )}
                        
                        {/* Form Link (Purple Accent) */}
                        {product.form_link && (
                            <a 
                                href={product.form_link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="group flex items-center justify-center gap-3 bg-purple-500/10 border border-purple-500 text-purple-400 font-bold py-4 px-6 hover:bg-purple-500/20 transition w-full mt-4 uppercase tracking-widest text-xs"
                                onClick={() => trackGAEvent('Engagement', 'Form Link Click', product.form_name)}
                            >
                                {product.form_name || "Access Form Protocol"}
                            </a>
                        )}
                    </div>

                    {/* Social Share */}
                    <div className="mt-auto pt-8 border-t border-[#22d3ee]/10">
                        <p className="text-[10px] font-bold text-[#22d3ee] mb-4 uppercase tracking-widest flex items-center gap-2">
                            <Share2 size={12}/> Transmit Data
                        </p>
                        <div className="flex gap-4">
                            <SocialIcon onClick={() => trackGAEvent('Social', 'Share', 'Facebook')} href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`} icon={<FaFacebook/>} color="hover:text-blue-500 hover:border-blue-500" />
                            <SocialIcon onClick={() => trackGAEvent('Social', 'Share', 'Twitter')} href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${encodeURIComponent(product.title)}`} icon={<FaTwitter/>} color="hover:text-sky-400 hover:border-sky-400" />
                            <SocialIcon onClick={() => trackGAEvent('Social', 'Share', 'LinkedIn')} href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentUrl}`} icon={<FaLinkedin/>} color="hover:text-blue-600 hover:border-blue-600" />
                            <SocialIcon onClick={() => trackGAEvent('Social', 'Share', 'WhatsApp')} href={`https://api.whatsapp.com/send?text=${encodeURIComponent(product.title + ' ' + currentUrl)}`} icon={<FaWhatsapp/>} color="hover:text-green-500 hover:border-green-500" />
                        </div>
                    </div>
                </div>
            </div>

            {/* --- BOTTOM SECTION: DESCRIPTION --- */}
            <div className="p-8 md:p-12 border-t border-[#22d3ee]/10 bg-[#0f172a]">
                <div className="flex items-center gap-3 mb-6">
                    <Cpu className="text-[#22d3ee]"/>
                    <h3 className="text-lg font-bold text-white uppercase tracking-widest">System Specifications</h3>
                </div>
                {/* prose-invert makes typography look good on dark backgrounds */}
                <div className="prose prose-invert prose-sm lg:prose-base max-w-none text-slate-400 font-sans" dangerouslySetInnerHTML={{ __html: product.content }} />
            </div>

          </div>

          {/* Comments Section */}
          <div className="bg-[#0f172a] border border-[#22d3ee]/20 mt-8 p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 opacity-20">
                <HiChatAlt2 size={100} className="text-[#22d3ee]" />
            </div>
            
            <h2 className="text-xl font-bold mb-8 flex items-center gap-3 text-white uppercase tracking-widest border-b border-[#22d3ee]/20 pb-4 w-fit">
              User Feedback <span className="text-[#22d3ee] text-sm">({product.comments?.length || 0})</span>
            </h2>

            <div className="space-y-6 mb-12">
              {product.comments && product.comments.length > 0 ? (
                product.comments.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-4 group">
                    <div className="bg-[#22d3ee]/10 border border-[#22d3ee]/30 p-3 rounded-sm flex-shrink-0 text-[#22d3ee]">
                      <HiUser />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                          <p className="font-bold text-white text-sm uppercase tracking-wide">{comment.username}</p>
                          <span className="text-[10px] text-slate-600 bg-slate-900 px-1 border border-slate-800">VERIFIED</span>
                      </div>
                      <p className="text-slate-400 mt-1 font-mono text-sm border-l-2 border-slate-700 pl-3 group-hover:border-[#22d3ee] transition-colors">{comment.comment}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-600 font-mono italic text-sm border border-dashed border-slate-700 p-4 text-center">
                    // NO_DATA_FOUND: Be the first to initialize a review protocol.
                </p>
              )}
            </div>

            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="space-y-4 max-w-3xl">
              <div className="relative">
                 <div className="absolute top-3 left-3 text-[#22d3ee]"><Terminal size={16}/></div>
                 <textarea 
                    value={newComment} 
                    onChange={(e) => setNewComment(e.target.value)} 
                    className="w-full pl-10 p-3 bg-[#020617] text-slate-300 border border-slate-700 focus:border-[#22d3ee] focus:ring-1 focus:ring-[#22d3ee] focus:outline-none transition-all font-mono text-sm h-32 placeholder-slate-600" 
                    placeholder="> Input feedback data..." 
                    required
                 ></textarea>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    value={username} 
                    onChange={(e) => setUsername(e.target.value)} 
                    className="w-full p-3 bg-[#020617] text-slate-300 border border-slate-700 focus:border-[#22d3ee] focus:ring-1 focus:ring-[#22d3ee] focus:outline-none transition-all font-mono text-sm placeholder-slate-600" 
                    placeholder="> User ID (Name)" 
                    required 
                  />
                  <input 
                    type="text" 
                    value={contact} 
                    onChange={(e) => setContact(e.target.value)} 
                    className="w-full p-3 bg-[#020617] text-slate-300 border border-slate-700 focus:border-[#22d3ee] focus:ring-1 focus:ring-[#22d3ee] focus:outline-none transition-all font-mono text-sm placeholder-slate-600" 
                    placeholder="> Comm Link (Contact - Optional)" 
                  />
              </div>
              <button type="submit" className="bg-[#22d3ee] hover:bg-[#06b6d4] text-[#020617] font-bold py-3 px-8 transition uppercase tracking-widest text-xs flex items-center gap-2">
                <HiLightningBolt /> Submit Log
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

// Helper for Social Icons
const SocialIcon = ({ href, icon, color, onClick }) => (
    <a 
        onClick={onClick} 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className={`w-10 h-10 flex items-center justify-center border border-slate-700 bg-[#020617] text-slate-500 transition-all duration-300 ${color}`}
    >
        {icon}
    </a>
);

export default ProductDetails;